import { capitalize } from "lodash";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { useAiAdvisor, useAuthActions, useLangSwitch } from "@ryde/redux";
import {
  appLanguageSelector,
  isCompanySelectDialogAvailableSelector,
  ISettingsSubItem,
  profileModalOpenStateAtom,
} from "@ryde/shared";

import { useCreateSettingsItem } from "../data";
import { SettingsGroup } from "../settings.group";
import {
  ImgExitButton,
  ImgHelpPageRedirectButton,
  ImgLangEnSvg,
  ImgLangHeSvg,
  ImgProfileButton,
} from "./mobile.settings.styled";

export const MobileLangSwitch: FC = () => {
  const { createMobileHandleMenuItem } = useCreateSettingsItem();
  const profileModalSetOpenState = useSetRecoilState(profileModalOpenStateAtom);
  const isCompanySelectDialogAvailable = useRecoilValue(
    isCompanySelectDialogAvailableSelector,
  );
  const { openBulkAIAdvisorDrawer } = useAiAdvisor("unknown");
  const { t } = useTranslation();
  const { switchLangUpdateProfile } = useLangSwitch();
  const language = useRecoilValue(appLanguageSelector);
  const { handleLogout } = useAuthActions();
  const switchLangToEnglish = useEvent(() => {
    switchLangUpdateProfile("en");
  });

  const switchLangToHebrew = useEvent(() => {
    switchLangUpdateProfile("he");
  });

  const openProfile = useEvent(() => {
    profileModalSetOpenState(true);
  });

  const settingSubItems: ISettingsSubItem[] = useMemo(() => {
    const items = [
      createMobileHandleMenuItem({
        title: t("general.en"),
        onClick: switchLangToEnglish,
        customRightImage: ImgLangEnSvg(),
        active: language === "en",
      }),
      createMobileHandleMenuItem({
        title: t("general.he"),
        onClick: switchLangToHebrew,
        customRightImage: ImgLangHeSvg(),
        active: language === "he",
      }),
      createMobileHandleMenuItem({
        title: t("chatBot.single"),
        onClick: openBulkAIAdvisorDrawer,
        customRightImage: ImgHelpPageRedirectButton({
          onClick: openBulkAIAdvisorDrawer,
        }),
      }),
      createMobileHandleMenuItem({
        title: t("general.LOGOUT"),
        onClick: handleLogout,
        customRightImage: ImgExitButton({ onClick: handleLogout }),
      }),
      ...(isCompanySelectDialogAvailable
        ? [
            createMobileHandleMenuItem({
              title: t("general.PROFILE"),
              onClick: openProfile,
              customRightImage: ImgProfileButton({ onClick: openProfile }),
            }),
          ]
        : []),
    ];
    return items;
  }, [
    createMobileHandleMenuItem,
    handleLogout,
    isCompanySelectDialogAvailable,
    language,
    openBulkAIAdvisorDrawer,
    openProfile,
    switchLangToEnglish,
    switchLangToHebrew,
    t,
  ]);

  return (
    <SettingsGroup
      key={"application_manage"}
      groupTitle={capitalize(t(`general.ADMIN_FUNCTIONS`))}
      groupItems={settingSubItems}
    ></SettingsGroup>
  );
};
